/**
 * 크롤링 로그 페이지
 * MySQL에 저장된 크롤링 실행 로그 조회
 */

import { useState, useEffect } from 'react';
import {
    Box,
    Heading,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Button,
    HStack,
    VStack,
    Text,
    Select,
    Input,
    Stat,
    StatLabel,
    StatNumber,
    StatGroup,
    SimpleGrid,
    useToast,
    IconButton,
    Tooltip,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    useDisclosure,
    Alert,
    AlertIcon,
    Spinner,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Textarea,
    Code,
    IconButton,
} from '@chakra-ui/react';
import { DeleteIcon, RepeatIcon, WarningIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import axios from 'axios';
import { hasPermission, type Role } from '../config/permissions';

interface CrawlerLog {
    id: number;
    site_name: string;
    site_url: string;
    status: 'SUCCESS' | 'PARTIAL' | 'FAILED';
    items_found: number;
    items_crawled: number;
    items_skipped: number;
    items_failed: number;
    started_at: string;
    completed_at: string;
    duration_seconds: number;
    error_message: string | null;
    execution_type: 'MANUAL' | 'SCHEDULED' | 'INITIAL';
}

interface CrawlerStats {
    siteStats: Array<{
        site_name: string;
        total_executions: number;
        success_count: number;
        failed_count: number;
        total_items_crawled: number;
        total_items_skipped: number;
        avg_duration_seconds: number;
        last_crawled_at: string;
    }>;
    recentRuns24h: number;
    statusStats: Array<{
        status: string;
        count: number;
    }>;
}

export default function CrawlerLogsPage() {
    const [logs, setLogs] = useState<CrawlerLog[]>([]);
    const [stats, setStats] = useState<CrawlerStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        site_name: '',
        status: '',
        execution_type: ''
    });
    
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedLog, setSelectedLog] = useState<CrawlerLog | null>(null);
    
    // 권한 확인
    const userRole = (localStorage.getItem('userRole') || 'admin') as Role;
    const isAdmin = userRole === 'admin';
    
    // 크롤링 설정 상태
    const [crawlConfig, setCrawlConfig] = useState<string>('');
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);
    const [configMeta, setConfigMeta] = useState<{updated_at?: string, updated_by?: string}>({});

    // 로그 조회
    const fetchLogs = async () => {
        try {
            setLoading(true);
            
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '10',  // 페이징 UI 테스트를 위해 50 → 10으로 변경
                ...(filters.site_name && { site_name: filters.site_name }),
                ...(filters.status && { status: filters.status }),
                ...(filters.execution_type && { execution_type: filters.execution_type })
            });
            
            const response = await axios.get(`/graph/crawler-logs?${params}`, {
                withCredentials: true
            });
            
            if (response.data.success) {
                setLogs(response.data.logs);
                setTotalPages(response.data.pagination.totalPages);
            }
        } catch (error: any) {
            if (error.response?.status === 501) {
                toast({
                    title: 'MySQL 환경 필요',
                    description: 'SQLite 환경에서는 사용할 수 없습니다.',
                    status: 'warning',
                    duration: 5000,
                });
            } else {
                toast({
                    title: '로그 조회 실패',
                    description: error.response?.data?.message || '오류 발생',
                    status: 'error',
                    duration: 5000,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    // 통계 조회
    const fetchStats = async () => {
        try {
            const response = await axios.get('/graph/crawler-stats', {
                withCredentials: true
            });
            
            if (response.data.success) {
                setStats(response.data);
            }
        } catch (error: any) {
            console.error('통계 조회 실패:', error);
            // 빈 통계로 초기화하여 렌더링 에러 방지
            setStats({
                siteStats: [],
                recentRuns24h: 0,
                statusStats: []
            });
        }
    };

    // 로그 삭제
    const handleDelete = async (id: number) => {
        if (!confirm('이 로그를 삭제하시겠습니까?')) return;
        
        try {
            await axios.delete(`/graph/crawler-logs/${id}`, {
                withCredentials: true
            });
            
            toast({
                title: '로그 삭제 완료',
                status: 'success',
                duration: 3000,
            });
            
            fetchLogs();
        } catch (error) {
            toast({
                title: '로그 삭제 실패',
                status: 'error',
                duration: 5000,
            });
        }
    };

    // 오래된 로그 정리
    const handleCleanup = async () => {
        if (!confirm('30일 이상 된 로그를 모두 삭제하시겠습니까?')) return;
        
        try {
            const response = await axios.delete('/graph/crawler-logs/cleanup?days=30', {
                withCredentials: true
            });
            
            toast({
                title: '로그 정리 완료',
                description: `${response.data.deletedCount}개 로그 삭제됨`,
                status: 'success',
                duration: 5000,
            });
            
            fetchLogs();
            fetchStats();
        } catch (error) {
            toast({
                title: '로그 정리 실패',
                status: 'error',
                duration: 5000,
            });
        }
    };

    // 수동 크롤링 실행
    const handleManualCrawl = async (siteName?: string) => {
        if (!confirm(siteName 
            ? `"${siteName}" 사이트를 크롤링하시겠습니까?` 
            : '전체 사이트를 크롤링하시겠습니까? (시간이 오래 걸릴 수 있습니다)')) {
            return;
        }
        
        try {
            const response = await axios.post('/graph/crawler-logs/run', {
                siteName: siteName || null,
                maxPages: 1  // 기본 1페이지만
            }, {
                withCredentials: true
            });
            
            toast({
                title: '크롤링 시작',
                description: response.data.message,
                status: 'info',
                duration: 5000,
                isClosable: true,
            });
            
            // 5초 후 자동 새로고침
            setTimeout(() => {
                fetchLogs();
                fetchStats();
            }, 5000);
        } catch (error: any) {
            toast({
                title: '크롤링 실행 실패',
                description: error.response?.data?.message || '오류 발생',
                status: 'error',
                duration: 5000,
            });
        }
    };

    // 페이지 번호 클릭 핸들러
    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    // 표시할 페이지 번호 계산 (최대 5개 표시)
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;
        
        if (totalPages <= maxPagesToShow) {
            // 전체 페이지가 5개 이하면 모두 표시
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // 현재 페이지를 중심으로 앞뒤 페이지 계산
            let startPage = Math.max(1, page - 2);
            let endPage = Math.min(totalPages, page + 2);
            
            // 시작이 1이 아니면 첫 페이지와 생략 표시
            if (startPage > 1) {
                pages.push(1);
                if (startPage > 2) {
                    pages.push('...');
                }
            }
            
            // 중간 페이지들 (최대 5개)
            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }
            
            // 끝이 마지막 페이지가 아니면 생략 표시와 마지막 페이지
            if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                    pages.push('...');
                }
                pages.push(totalPages);
            }
        }
        
        return pages;
    };

    useEffect(() => {
        fetchLogs();
        fetchStats();
    }, [page, filters]);

    // 상태 배지 색상
    const getStatusColor = (status: string) => {
        switch (status) {
            case 'SUCCESS': return 'green';
            case 'PARTIAL': return 'yellow';
            case 'FAILED': return 'red';
            default: return 'gray';
        }
    };

    // 실행 유형 배지 색상
    const getExecutionTypeColor = (type: string) => {
        switch (type) {
            case 'MANUAL': return 'blue';
            case 'SCHEDULED': return 'purple';
            case 'INITIAL': return 'orange';
            default: return 'gray';
        }
    };
    
    // 크롤링 설정 가져오기
    const fetchCrawlConfig = async () => {
        try {
            setConfigLoading(true);
            const response = await axios.get('/graph/crawler-config', { withCredentials: true });
            if (response.data.success) {
                setCrawlConfig(JSON.stringify(response.data.config, null, 2));
                setConfigMeta({
                    updated_at: response.data.updated_at,
                    updated_by: response.data.updated_by
                });
            }
        } catch (error: any) {
            toast({
                title: '설정 조회 실패',
                description: error.response?.data?.message || '오류 발생',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setConfigLoading(false);
        }
    };
    
    // 크롤링 설정 저장
    const saveCrawlConfig = async () => {
        try {
            // JSON 유효성 검증
            const config = JSON.parse(crawlConfig);
            
            setConfigSaving(true);
            const response = await axios.put('/graph/crawler-config', 
                { config }, 
                { withCredentials: true }
            );
            
            if (response.data.success) {
                toast({
                    title: '설정 저장 완료',
                    description: '크롤링 설정이 저장되었습니다.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error: any) {
            if (error instanceof SyntaxError) {
                toast({
                    title: 'JSON 형식 오류',
                    description: 'JSON 형식이 올바르지 않습니다.',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: '설정 저장 실패',
                    description: error.response?.data?.message || '오류 발생',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } finally {
            setConfigSaving(false);
        }
    };

    // 로그 상세 보기
    const handleViewDetails = (log: CrawlerLog) => {
        setSelectedLog(log);
        onOpen();
    };

    return (
        <Box>
            {/* 페이지 헤더 */}
            <Box mb={6}>
                <Heading size="lg" color="blue.700" mb={2}>
                    📊 웹 크롤링 관리
                </Heading>
                <Text color="gray.600">
                    웹사이트 크롤링 실행 로그와 설정을 관리합니다.
                </Text>
            </Box>
            
            <Tabs colorScheme="bexco">
                <TabList>
                    <Tab>크롤링 로그</Tab>
                    {isAdmin && <Tab onClick={fetchCrawlConfig}>크롤링 설정</Tab>}
                </TabList>
                
                <TabPanels>
                    {/* 크롤링 로그 탭 */}
                    <TabPanel>
                        <HStack justifyContent="flex-end" mb={6}>
                    {isAdmin && (
                        <Button
                            colorScheme="blue"
                            onClick={() => handleManualCrawl()}
                            size="sm"
                        >
                            🚀 수동 크롤링 실행
                        </Button>
                    )}
                    <Button
                        leftIcon={<RepeatIcon />}
                        onClick={() => { fetchLogs(); fetchStats(); }}
                        size="sm"
                    >
                        새로고침
                    </Button>
                    {isAdmin && (
                        <Button
                            leftIcon={<WarningIcon />}
                            onClick={handleCleanup}
                            colorScheme="red"
                            variant="outline"
                            size="sm"
                        >
                            오래된 로그 정리
                        </Button>
                    )}
                </HStack>

            {/* 통계 */}
            {stats && (
                <SimpleGrid columns={[1, 2, 4]} spacing={4} mb={6}>
                    <Stat>
                        <StatLabel>최근 24시간 실행</StatLabel>
                        <StatNumber>{stats.recentRuns24h}회</StatNumber>
                    </Stat>
                    
                    <Stat>
                        <StatLabel>성공</StatLabel>
                        <StatNumber color="green.500">
                            {stats.statusStats?.find(s => s.status === 'SUCCESS')?.count || 0}
                        </StatNumber>
                    </Stat>
                    
                    <Stat>
                        <StatLabel>부분 성공</StatLabel>
                        <StatNumber color="yellow.500">
                            {stats.statusStats?.find(s => s.status === 'PARTIAL')?.count || 0}
                        </StatNumber>
                    </Stat>
                    
                    <Stat>
                        <StatLabel>실패</StatLabel>
                        <StatNumber color="red.500">
                            {stats.statusStats?.find(s => s.status === 'FAILED')?.count || 0}
                        </StatNumber>
                    </Stat>
                </SimpleGrid>
            )}

            {/* 필터 */}
            <HStack mb={4} spacing={4}>
                <Input
                    placeholder="사이트명 검색"
                    value={filters.site_name}
                    onChange={(e) => setFilters({ ...filters, site_name: e.target.value })}
                    maxW="200px"
                />
                
                <Select
                    placeholder="상태"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    maxW="150px"
                >
                    <option value="SUCCESS">성공</option>
                    <option value="PARTIAL">부분 성공</option>
                    <option value="FAILED">실패</option>
                </Select>
                
                <Select
                    placeholder="실행 유형"
                    value={filters.execution_type}
                    onChange={(e) => setFilters({ ...filters, execution_type: e.target.value })}
                    maxW="150px"
                >
                    <option value="MANUAL">수동</option>
                    <option value="SCHEDULED">자동</option>
                    <option value="INITIAL">초기화</option>
                </Select>
            </HStack>

            {/* 로그 테이블 */}
            {loading ? (
                <Box textAlign="center" py={10}>
                    <Spinner size="xl" />
                </Box>
            ) : logs.length === 0 ? (
                <Alert status="info">
                    <AlertIcon />
                    로그가 없습니다.
                </Alert>
            ) : (
                <>
                    <Box overflowX="auto" bg="white" borderRadius="lg" shadow="sm">
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>ID</Th>
                                    <Th>사이트명</Th>
                                    <Th>상태</Th>
                                    <Th>유형</Th>
                                    <Th>발견/크롤링/스킵/실패</Th>
                                    <Th>시작 시간</Th>
                                    <Th>소요 시간</Th>
                                    <Th>작업</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {logs.map((log) => (
                                    <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                                        <Td>{log.id}</Td>
                                        <Td>
                                            <Text fontWeight="medium">{log.site_name}</Text>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={getStatusColor(log.status)}>
                                                {log.status}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={getExecutionTypeColor(log.execution_type)}>
                                                {log.execution_type}
                                            </Badge>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">
                                                <Text as="span" color="gray.600">{log.items_found}</Text>
                                                {' / '}
                                                <Text as="span" color="green.600">{log.items_crawled}</Text>
                                                {' / '}
                                                <Text as="span" color="orange.600">{log.items_skipped}</Text>
                                                {' / '}
                                                <Text as="span" color="red.600">{log.items_failed}</Text>
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">
                                                {new Date(log.started_at).toLocaleString('ko-KR')}
                                            </Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">{log.duration_seconds}초</Text>
                                        </Td>
                                        <Td>
                                            <HStack spacing={2}>
                                                <Tooltip label="상세 보기">
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleViewDetails(log)}
                                                    >
                                                        보기
                                                    </Button>
                                                </Tooltip>
                                                {isAdmin && (
                                                    <Tooltip label="삭제">
                                                        <IconButton
                                                            aria-label="삭제"
                                                            icon={<DeleteIcon />}
                                                            size="sm"
                                                            colorScheme="red"
                                                            variant="ghost"
                                                            onClick={() => handleDelete(log.id)}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>

                    {/* 페이지네이션 */}
                    {totalPages > 1 && (
                        <HStack justify="flex-end" mt={4} spacing={1}>
                            {/* 첫 페이지 버튼 */}
                            <Button
                                aria-label="첫 페이지"
                                size="sm"
                                variant="ghost"
                                isDisabled={page === 1}
                                onClick={() => handlePageChange(1)}
                                minW="40px"
                            >
                                ««
                            </Button>
                            {/* 이전 페이지 버튼 */}
                            <IconButton
                                aria-label="이전 페이지"
                                icon={<ChevronLeftIcon />}
                                size="sm"
                                variant="ghost"
                                isDisabled={page === 1}
                                onClick={() => handlePageChange(page - 1)}
                            />
                            {/* 페이지 번호 버튼들 */}
                            {getPageNumbers().map((pageNum, index) => {
                                if (pageNum === '...') {
                                    return (
                                        <Text key={`ellipsis-${index}`} fontSize="sm" px={2} color="gray.500">
                                            ...
                                        </Text>
                                    );
                                }
                                const isActive = pageNum === page;
                                return isActive ? (
                                    <Button
                                        key={pageNum}
                                        size="sm"
                                        variant="outline"
                                        colorScheme="blue"
                                        onClick={() => handlePageChange(pageNum as number)}
                                        minW="40px"
                                        fontWeight="bold"
                                        cursor="default"
                                    >
                                        {pageNum}
                                    </Button>
                                ) : (
                                    <Button
                                        key={pageNum}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handlePageChange(pageNum as number)}
                                        minW="40px"
                                        color="gray.600"
                                        _hover={{ bg: 'gray.100' }}
                                    >
                                        {pageNum}
                                    </Button>
                                );
                            })}
                            {/* 다음 페이지 버튼 */}
                            <IconButton
                                aria-label="다음 페이지"
                                icon={<ChevronRightIcon />}
                                size="sm"
                                variant="ghost"
                                isDisabled={page === totalPages}
                                onClick={() => handlePageChange(page + 1)}
                            />
                            {/* 마지막 페이지 버튼 */}
                            <Button
                                aria-label="마지막 페이지"
                                size="sm"
                                variant="ghost"
                                isDisabled={page === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                                minW="40px"
                            >
                                »»
                            </Button>
                        </HStack>
                    )}
                </>
            )}

            {/* 상세 모달 */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>크롤링 로그 상세</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedLog && (
                            <VStack align="stretch" spacing={4}>
                                <Box>
                                    <Text fontWeight="bold" mb={2}>사이트명</Text>
                                    <Text>{selectedLog.site_name}</Text>
                                </Box>
                                
                                <Box>
                                    <Text fontWeight="bold" mb={2}>URL</Text>
                                    <Text fontSize="sm" color="blue.500" wordBreak="break-all">
                                        {selectedLog.site_url}
                                    </Text>
                                </Box>
                                
                                <SimpleGrid columns={2} spacing={4}>
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>상태</Text>
                                        <Badge colorScheme={getStatusColor(selectedLog.status)}>
                                            {selectedLog.status}
                                        </Badge>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>실행 유형</Text>
                                        <Badge colorScheme={getExecutionTypeColor(selectedLog.execution_type)}>
                                            {selectedLog.execution_type}
                                        </Badge>
                                    </Box>
                                </SimpleGrid>
                                
                                <SimpleGrid columns={2} spacing={4}>
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>발견</Text>
                                        <Text>{selectedLog.items_found}개</Text>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>크롤링 성공</Text>
                                        <Text color="green.600">{selectedLog.items_crawled}개</Text>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>중복 스킵</Text>
                                        <Text color="orange.600">{selectedLog.items_skipped}개</Text>
                                    </Box>
                                    
                                    <Box>
                                        <Text fontWeight="bold" mb={2}>실패</Text>
                                        <Text color="red.600">{selectedLog.items_failed}개</Text>
                                    </Box>
                                </SimpleGrid>
                                
                                <Box>
                                    <Text fontWeight="bold" mb={2}>시작 시간</Text>
                                    <Text>{new Date(selectedLog.started_at).toLocaleString('ko-KR')}</Text>
                                </Box>
                                
                                <Box>
                                    <Text fontWeight="bold" mb={2}>완료 시간</Text>
                                    <Text>{new Date(selectedLog.completed_at).toLocaleString('ko-KR')}</Text>
                                </Box>
                                
                                <Box>
                                    <Text fontWeight="bold" mb={2}>소요 시간</Text>
                                    <Text>{selectedLog.duration_seconds}초</Text>
                                </Box>
                                
                                {selectedLog.error_message && (
                                    <Box>
                                        <Text fontWeight="bold" mb={2} color="red.500">오류 메시지</Text>
                                        <Box p={3} bg="red.50" borderRadius="md">
                                            <Text fontSize="sm" color="red.700">
                                                {selectedLog.error_message}
                                            </Text>
                                        </Box>
                                    </Box>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onClose}>닫기</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
                    </TabPanel>
                    
                    {/* 크롤링 설정 탭 */}
                    {isAdmin && <TabPanel>
                        <VStack spacing={4} align="stretch">
                            <Alert status="info">
                                <AlertIcon />
                                <Box>
                                    <Text fontWeight="bold">크롤링 설정 편집 (MySQL DB 저장)</Text>
                                    <Text fontSize="sm">
                                        JSON 형식으로 크롤링 설정을 수정할 수 있습니다. 
                                        설정은 MySQL DB에 저장되며, 변경 후 즉시 적용됩니다. (Docker 재빌드 불필요!)
                                    </Text>
                                    {configMeta.updated_at && (
                                        <Text fontSize="xs" color="gray.600" mt={1}>
                                            마지막 수정: {new Date(configMeta.updated_at).toLocaleString('ko-KR')} 
                                            {configMeta.updated_by && ` (by ${configMeta.updated_by})`}
                                        </Text>
                                    )}
                                </Box>
                            </Alert>
                            
                            {configLoading ? (
                                <Box textAlign="center" py={10}>
                                    <Spinner size="xl" />
                                    <Text mt={4}>설정 불러오는 중...</Text>
                                </Box>
                            ) : (
                                <>
                                    <Box>
                                        <HStack justifyContent="space-between" mb={2}>
                                            <Text fontWeight="bold">crawl-config.json</Text>
                                            <HStack>
                                                <Button
                                                    size="sm"
                                                    onClick={fetchCrawlConfig}
                                                    isLoading={configLoading}
                                                >
                                                    🔄 새로고침
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    colorScheme="green"
                                                    onClick={saveCrawlConfig}
                                                    isLoading={configSaving}
                                                >
                                                    💾 저장
                                                </Button>
                                            </HStack>
                                        </HStack>
                                        
                                        <Textarea
                                            value={crawlConfig}
                                            onChange={(e) => setCrawlConfig(e.target.value)}
                                            fontFamily="monospace"
                                            fontSize="sm"
                                            minHeight="500px"
                                            placeholder="설정을 불러오는 중..."
                                            bg="gray.50"
                                        />
                                    </Box>
                                    
                                    <Box p={4} bg="yellow.50" borderRadius="md">
                                        <Text fontWeight="bold" mb={2}>⚠️ 주의사항</Text>
                                        <VStack align="start" spacing={1} fontSize="sm">
                                            <Text>• JSON 형식이 올바르지 않으면 저장되지 않습니다.</Text>
                                            <Text>• 설정은 MySQL DB에 저장되며, 이전 버전은 <Code>updated_at</Code>으로 추적 가능합니다.</Text>
                                            <Text>• <Code>enabled: false</Code>로 설정하면 해당 사이트는 크롤링하지 않습니다.</Text>
                                            <Text>• <Code>maxPages</Code>를 조절하여 크롤링 페이지 수를 제한할 수 있습니다.</Text>
                                            <Text>• 설정 변경 후 즉시 적용되며, 재시작 불필요합니다.</Text>
                                        </VStack>
                                    </Box>
                                </>
                            )}
                        </VStack>
                    </TabPanel>}
                </TabPanels>
            </Tabs>
        </Box>
    );
}

